import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { deepMerge } from '../core/deepMerge';
import { bindLabel, createLineLabelContext, createPointLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { createPoint } from '../point/point.create';
import type { ElementTheme, LineStyle, PointStyle } from '../theme/theme.model';
import type { LineArgs, LineResult, PointInput } from './line.model';

/**
 * Resolve a theme color name to a CSS color string.
 */
function resolveColor(themeService: JsxgraphThemeService, color: ThemeColorName | undefined): string | undefined {
  if (!color) {
    return undefined;
  }
  return themeService.getColor(color);
}

/**
 * Check if input is an existing JSXGraph element.
 */
function isElement(input: PointInput): input is GeometryElement {
  return typeof input === 'object' && !Array.isArray(input) && 'elType' in input;
}

/**
 * Resolve vertex style with proper propagation order:
 * 1. theme.point
 * 2. theme.line.vertex
 * 3. style.vertex
 * 4. style.vertexPerIndex[index]
 */
function resolveVertexStyle(
  theme: ElementTheme,
  lineVertex?: PointStyle,
  styleVertex?: PointStyle,
  perIndex?: PointStyle,
): PointStyle {
  let result: PointStyle = { ...theme.point };
  result = deepMerge(result, theme.line.vertex);
  result = deepMerge(result, lineVertex);
  result = deepMerge(result, styleVertex);
  result = deepMerge(result, perIndex);
  return result;
}

/**
 * Build JSXGraph line attributes from style configuration.
 */
function buildLineAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['line'],
  styleOverrides?: LineStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);
  const strokeColor = resolveColor(themeService, style.color);

  const attrs: Record<string, unknown> = {
    strokeColor,
    strokeWidth: style.strokeWidth,
    dash: style.dash,
    visible: style.visible,
    // Pass through JSXGraph's infinite line controls (default to false = segment)
    straightFirst: style.straightFirst ?? false,
    straightLast: style.straightLast ?? false,
    withLabel: false,
  };

  // Handle arrows
  if (styleOverrides?.arrows) {
    attrs['firstArrow'] = styleOverrides.arrows.first ?? false;
    attrs['lastArrow'] = styleOverrides.arrows.last ?? false;
  }

  return attrs;
}

/**
 * Create a line element with optional vertices and labels.
 * Labels are now configured via the style property:
 * - style.label: Label for the line itself
 * - style.vertex.label: Default label for both vertices
 * - style.vertexPerIndex[0].label / style.vertexPerIndex[1].label: Per-vertex label overrides
 *
 * Style propagation order for vertices:
 * 1. theme.point (base point defaults)
 * 2. theme.line.vertex (line-specific vertex defaults)
 * 3. style.vertex (user-provided vertex defaults)
 * 4. style.vertexPerIndex[0|1] (per-vertex overrides)
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Line creation arguments
 * @returns Line result with elements and optional labels
 */
export function createLine(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: LineArgs,
): LineResult {
  const { board } = ctx;
  const { a: inputA, b: inputB, name = '', style, vertexNames = ['A', 'B'] } = args;

  // Resolve or create vertices
  let pointA: GeometryElement;
  let pointB: GeometryElement;

  // Resolve vertex A
  if (isElement(inputA)) {
    pointA = inputA;
  } else if (typeof inputA === 'string') {
    // Look up point by name
    const found = board.elementsByName[inputA];
    if (!found) {
      throw new Error(`Point "${inputA}" not found on board`);
    }
    pointA = found as GeometryElement;
  } else {
    // Create new point from coords
    const vertexStyleA = resolveVertexStyle(theme, style?.vertex, style?.vertex, style?.vertexPerIndex?.[0]);
    const result = createPoint(ctx, themeService, theme, {
      coords: inputA,
      name: vertexNames[0],
      style: vertexStyleA,
    });
    pointA = result.point;
  }

  // Resolve vertex B
  if (isElement(inputB)) {
    pointB = inputB;
  } else if (typeof inputB === 'string') {
    const found = board.elementsByName[inputB];
    if (!found) {
      throw new Error(`Point "${inputB}" not found on board`);
    }
    pointB = found as GeometryElement;
  } else {
    const vertexStyleB = resolveVertexStyle(theme, style?.vertex, style?.vertex, style?.vertexPerIndex?.[1]);
    const result = createPoint(ctx, themeService, theme, {
      coords: inputB,
      name: vertexNames[1],
      style: vertexStyleB,
    });
    pointB = result.point;
  }

  // Build line attributes
  const lineAttrs = buildLineAttributes(themeService, theme.line, style);

  // Create the line (using 'line' element with straightFirst/straightLast controlling infinite extension)
  const line = board.create('line', [pointA, pointB], lineAttrs) as GeometryElement;

  const result: LineResult = { line, a: pointA, b: pointB };

  // Determine color for labels
  const labelColor = style?.color ?? 'neutral';

  // Resolve vertex label configs from style hierarchy
  // Priority: style.vertexPerIndex[i].label > style.vertex.label
  const vertexALabelConfig = style?.vertexPerIndex?.[0]?.label ?? style?.vertex?.label;
  const vertexBLabelConfig = style?.vertexPerIndex?.[1]?.label ?? style?.vertex?.label;

  // Create label for vertex A if configured
  const labelConfigA = resolveLabelConfig(vertexALabelConfig, { color: labelColor });
  if (labelConfigA) {
    result.labelA = bindLabel({
      ctx,
      themeService,
      element: pointA,
      config: labelConfigA,
      contextProvider: createPointLabelContext(vertexNames[0], pointA),
    });
  }

  // Create label for vertex B if configured
  const labelConfigB = resolveLabelConfig(vertexBLabelConfig, { color: labelColor });
  if (labelConfigB) {
    result.labelB = bindLabel({
      ctx,
      themeService,
      element: pointB,
      config: labelConfigB,
      contextProvider: createPointLabelContext(vertexNames[1], pointB),
    });
  }

  // Create label for the line itself if configured - now from style.label
  const lineLabelConfig = resolveLabelConfig(style?.label, { color: labelColor });
  if (lineLabelConfig && name) {
    // For line labels, position at midpoint
    const midpoint = board.create('midpoint', [pointA, pointB], { visible: false }) as GeometryElement;
    result.lineLabel = bindLabel({
      ctx,
      themeService,
      element: midpoint,
      config: lineLabelConfig,
      contextProvider: createLineLabelContext(name, line),
    });
  }

  return result;
}
